import LayoutDashboard from "@/layouts/layoutDashboard";
import useAuth from "@/hooks/useAuth";
import EndorsementWidget from "@/components/pages/home/EndorsementWidget";
import RecommendedEventWidget from "@/components/pages/dashboard/RecommendedEventWidget";
import RecommendedCircleWidget from "@/components/pages/dashboard/RecommendedCircleWidget";
import { useGetUserProfile } from "@/hooks/useUsers";
import RecommendedConnectionWidget from "@/components/pages/dashboard/RecommendedConnectionWidget";
import CurrentProgress from "@/components/pages/home/CurrentProgress";
import MotivationalQuotes from "@/components/pages/home/MotivationalQuotes";
import QuickQuestionWidget from "@/components/pages/dashboard/QuickQuestionWidget";

import { GetUserViewQuery } from "@/graphql/graphql";
import { isMobile } from "react-device-detect";
import RewardsWidget from "@/components/pages/dashboard/RewardsWidget";
import Link from "next/link";
import { PAGE_URL } from "@/utils/constants";
import { Star } from "@/assets/icons";
import SwiperMC from "@/components/SwiperMC";
import Spinner from "@/components/Spinner";
import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { Button, Modal } from "antd";
import { getStarsInfo } from "@/utils/getStarsInfo";
import useEnvironmentStore from "@/store/useEnvironmentStore";
import useAuthStore from "@/store/useAuthStore";

type TUserHomepageConfig = Array<Array<string | null> | null> | null;

const Dashboard = () => {
  const router = useRouter();
  const [displayQuickQuestionReminder, setDisplayQuickQuestionReminder] =
    useState(false);

  const { user, isAuthenticated } = useAuthStore();
  const { refetch } = useGetUserProfile(
    user?.profile?.user_id,
    !isAuthenticated
  );

  const { data: dataUser, isLoading: isLoadingHomePageConfig } =
    useGetUserProfile(user?.profile?.user_id);

  const userHomepageConfig = dataUser?.userHomepageConfig;
  const userHomepageConfigMobile = dataUser?.userHomepageConfigMobile;
  const homepageConfig = isMobile
    ? userHomepageConfigMobile
    : userHomepageConfig;
  const disabledQuickQuestion = dataUser?.disabledQuickQuestion;

  const isOnboarded = useMemo(
    () => user?.bio?.user?.isOnboarded,
    [user?.bio?.user?.isOnboarded, router.query]
  );
  const hideDashboard = useMemo(() => !isOnboarded, [isOnboarded]);

  useEffect(() => {
    if (router.query?.ob && router.query?.ob === "done") {
      refetch();
    }
  }, [router.query]);

  useEffect(() => {
    if (
      disabledQuickQuestion === true &&
      router.query?.quickQuestionReminder === "true"
    ) {
      setDisplayQuickQuestionReminder(true);
    }
  }, [disabledQuickQuestion, router.query?.quickQuestionReminder]);

  const removeQuickQuestionReminderQueryUrl = () => {
    const { quickQuestionReminder, ...newQuery } = router.query;

    router.push(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true }
    );
  };

  return (
    <LayoutDashboard title={hideDashboard ? "" : "My Dashboard"}>
      {hideDashboard || isLoadingHomePageConfig ? (
        <div className="bg-card absolute top-0 left-0 right-0 bottom-0 bg-opacity-0 flex items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between pb-8">
            <h3 className="font-bold text-2xl">My Dashboard</h3>
            <div className="flex items-center gap-2">
              <Link
                href={PAGE_URL.dashboardSettings}
                className="!text-yellow cursor-pointer"
              >
                Edit
              </Link>
            </div>
          </div>
          {isMobile ? (
            <MobileMode
              homepageConfig={homepageConfig}
              dataGetUserView={user?.bio}
            />
          ) : (
            <DesktopMode
              homepageConfig={homepageConfig}
              dataGetUserView={user?.bio}
            />
          )}
        </>
      )}
      {displayQuickQuestionReminder && !isLoadingHomePageConfig && (
        <Modal
          footer={
            <>
              <Button
                onClick={() => {
                  setDisplayQuickQuestionReminder(false);
                  removeQuickQuestionReminderQueryUrl();
                }}
                type="default"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  router.push(PAGE_URL.dashboardSettings);
                }}
                type="primary"
              >
                Yes
              </Button>
            </>
          }
          title="Enable Quick Questions"
          open={displayQuickQuestionReminder}
          onCancel={() => setDisplayQuickQuestionReminder(false)}
        >
          You need to enable the Quick Questions section in order to answer the
          questions and highlight your favorites. Each answer helps build your
          favorites list and earn you points! <br /> <br />
          Do you want to enable Quick Questions on your Dashboard?
        </Modal>
      )}
      <div />
    </LayoutDashboard>
  );
};

function getWidgetFromWidgetKey(widgetKey: string | null, index: number) {
  switch (widgetKey) {
    case "ENDORSEMENT":
      return <EndorsementWidget key={index} />;
    case "GOAL_PROGRESS":
      return <CurrentProgress key={index} />;
    case "QUICK_QUESTION":
      return <QuickQuestionWidget key={index} />;
    default:
      return null;
  }
}

interface IMobileModeProps {
  homepageConfig?: TUserHomepageConfig;
  dataGetUserView: GetUserViewQuery;
}

function MobileMode({ homepageConfig, dataGetUserView }: IMobileModeProps) {
  const { env } = useEnvironmentStore();

  return (
    <div className="flex flex-col w-full gap-y-6">
      <MotivationalQuotes data={dataGetUserView} />
      {homepageConfig?.map(
        (row, rowIndex) =>
          rowIndex === 1 && (
            <div
              className="grid grid-cols-1 lg:flex gap-x-6"
              key={`widget-${rowIndex}-${row}`}
            >
              {row?.map((widgetKey, i) => getWidgetFromWidgetKey(widgetKey, i))}
            </div>
          )
      )}
      <div>
        <h3 className="font-bold text-2xl pb-2">Recommendations</h3>
        <div className="flex justify-between md:justify-end gap-x-2 w-full md:w-auto">
          {getStarsInfo(env).map((item, idx: number) => (
            <div
              className="flex items-start md:items-center gap-1"
              key={`label-${idx}`}
            >
              <Star size={isMobile ? 18 : 24} color={item?.color as never} />
              <p className="text-xs md:text-sm text-neutral-400">
                {item?.label}
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full">
        <SwiperMC
          props={{
            breakpoints: {
              0: {
                slidesPerView: 1.5,
                spaceBetween: 16,
              },
              768: {
                slidesPerView: 2.5,
                spaceBetween: 16,
              },
              1024: {
                slidesPerView: 2.3,
                spaceBetween: 16,
              },
              1440: {
                slidesPerView: 3.2,
                spaceBetween: 16,
              },
            },
            grabCursor: true,
          }}
          alwaysShowNavigation
          data={[
            <RecommendedConnectionWidget
              key={"RecommendedConnectionWidget"}
              wrapperClassName="min-h-[230px]"
            />,
            <RecommendedEventWidget
              key={"RecommendedEventWidget"}
              wrapperClassName="min-h-[230px]"
            />,
            <RecommendedCircleWidget
              key={"RecommendedCircleWidget"}
              wrapperClassName="min-h-[230px]"
            />,
          ]}
          render={(payload, i) => payload}
        />
      </div>
      {homepageConfig?.map(
        (row, rowIndex) =>
          rowIndex !== 0 &&
          rowIndex !== 1 &&
          rowIndex !== 2 &&
          rowIndex !== (homepageConfig?.length as number) - 1 && (
            <div className="flex gap-x-6 flex-col lg:flex-row" key={rowIndex}>
              {row?.map((widgetKey, i) => getWidgetFromWidgetKey(widgetKey, i))}
            </div>
          )
      )}
      <RewardsWidget />
    </div>
  );
}

interface IDesktopModeProps {
  homepageConfig?: TUserHomepageConfig;
  dataGetUserView: GetUserViewQuery;
}

function DesktopMode({ homepageConfig, dataGetUserView }: IDesktopModeProps) {
  const { env } = useEnvironmentStore();

  return (
    <div className="flex flex-col w-full gap-y-6">
      <MotivationalQuotes data={dataGetUserView} />
      {homepageConfig?.map(
        (row, rowIndex) =>
          rowIndex === 1 && (
            <div
              className="grid grid-cols-2 lg:flex gap-x-6"
              key={`widget-${rowIndex}-${row}`}
            >
              {row?.map((widgetKey, i) => getWidgetFromWidgetKey(widgetKey, i))}
              <div className="lg:hidden w-auto">
                <RecommendedConnectionWidget />
              </div>
            </div>
          )
      )}
      <div className="flex justify-between flex-wrap md:flex-nowrap gap-1 md:gap-0">
        {!isMobile && <h3 className="font-bold text-2xl">Recommendations</h3>}
        <div className="flex justify-between md:justify-end gap-x-2 w-full md:w-auto">
          {getStarsInfo(env).map((item, idx: number) => (
            <div
              className="flex items-start md:items-center  gap-1"
              key={`label-${idx}`}
            >
              <Star size={isMobile ? 18 : 24} color={item?.color as never} />
              <p className="text-xs md:text-sm text-neutral-400">
                {item?.label}
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className="gap-6 flex items-stretch">
        <RecommendedConnectionWidget />
        <div className="hidden lg:block lg:w-full h-auto">
          <RecommendedEventWidget />
        </div>
        <RecommendedCircleWidget />
      </div>
      {homepageConfig?.map(
        (row, rowIndex) =>
          rowIndex !== 0 &&
          rowIndex !== 1 &&
          rowIndex !== 2 &&
          rowIndex !== (homepageConfig?.length as number) - 1 && (
            <div
              className="flex gap-x-6 flex-col lg:flex-row"
              key={"homepage-" + rowIndex}
            >
              {row?.map((widgetKey, i) => getWidgetFromWidgetKey(widgetKey, i))}
            </div>
          )
      )}
      <RewardsWidget />
    </div>
  );
}

export default Dashboard;
